import { useCallback, useEffect, useState } from 'react';
import {
  vocintelApi,
  type IssueRecord,
  type ReturnIntelligence,
  type ReviewRecord,
  type SentimentSummary,
  type TimelineEntry,
  type TopicRecord,
  type VoCDashboard,
  type VoCRecommendation,
  type VocabularyInsight,
} from '../../api/vocintel';

type Tab = 'overview' | 'reviews' | 'sentiment' | 'topics' | 'issues' | 'returns' | 'vocabulary' | 'recommendations' | 'timeline';

function sentimentClass(s: string) {
  if (s === 'positive') return 'text-emerald-700 bg-emerald-50';
  if (s === 'negative') return 'text-red-700 bg-red-50';
  if (s === 'mixed') return 'text-amber-700 bg-amber-50';
  return 'text-slate-600 bg-slate-50';
}

function severityClass(s: string) {
  if (s === 'critical') return 'text-red-700 bg-red-50 border-red-200';
  if (s === 'high') return 'text-orange-700 bg-orange-50 border-orange-200';
  if (s === 'medium') return 'text-amber-700 bg-amber-50 border-amber-200';
  return 'text-slate-600 bg-slate-50 border-slate-200';
}

function stars(rating: number) {
  return '★'.repeat(rating) + '☆'.repeat(5 - rating);
}

export function VoCIntelPage() {
  const [tab, setTab] = useState<Tab>('overview');
  const [dashboard, setDashboard] = useState<VoCDashboard | null>(null);
  const [reviews, setReviews] = useState<ReviewRecord[]>([]);
  const [sentiment, setSentiment] = useState<SentimentSummary[]>([]);
  const [topics, setTopics] = useState<TopicRecord[]>([]);
  const [issues, setIssues] = useState<IssueRecord[]>([]);
  const [returns, setReturns] = useState<ReturnIntelligence[]>([]);
  const [vocabulary, setVocabulary] = useState<VocabularyInsight[]>([]);
  const [timeline, setTimeline] = useState<TimelineEntry[]>([]);
  const [recommendations, setRecommendations] = useState<VoCRecommendation[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [dash, rev, sent, top, iss, ret, voc, tl, recs] = await Promise.all([
        vocintelApi.dashboard(),
        vocintelApi.listReviews(),
        vocintelApi.listSentiment(),
        vocintelApi.listTopics(),
        vocintelApi.listIssues(),
        vocintelApi.listReturns(),
        vocintelApi.listVocabulary(),
        vocintelApi.listInsights(),
        vocintelApi.listRecommendations(),
      ]);
      setDashboard(dash);
      setReviews(rev);
      setSentiment(sent);
      setTopics(top);
      setIssues(iss);
      setReturns(ret);
      setVocabulary(voc);
      setTimeline(tl);
      setRecommendations(recs);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load voice of customer data');
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const generateRecs = async () => {
    setBusy(true);
    try { await vocintelApi.generateRecommendations(); await refresh(); }
    finally { setBusy(false); }
  };

  const tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'VoC Dashboard' },
    { id: 'reviews', label: 'Review Explorer' },
    { id: 'sentiment', label: 'Sentiment Explorer' },
    { id: 'topics', label: 'Topic Analysis' },
    { id: 'issues', label: 'Issue Center' },
    { id: 'returns', label: 'Return Intelligence' },
    { id: 'vocabulary', label: 'Customer Language' },
    { id: 'recommendations', label: 'Recommendation Workspace' },
    { id: 'timeline', label: 'VoC Timeline' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Voice of Customer Intelligence</h1>
          <p className="text-sm text-slate-500">Review intelligence, sentiment analysis &amp; customer experience — BCP-00006</p>
        </div>
        <button type="button" onClick={refresh} disabled={busy}
          className="rounded-lg bg-sky-600 px-4 py-2 text-sm font-medium text-white hover:bg-sky-700 disabled:opacity-50">
          Refresh
        </button>
      </div>

      {error && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

      <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-2">
        {tabs.map((t) => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium ${tab === t.id ? 'bg-sky-100 text-sky-700' : 'text-slate-600 hover:bg-slate-100'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && dashboard && (
        <div className="space-y-6">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { label: 'Total Reviews', value: dashboard.total_reviews.toLocaleString() },
              { label: 'Avg Rating', value: dashboard.avg_rating.toFixed(1) },
              { label: 'Customer Health', value: `${dashboard.customer_health_score.toFixed(0)}%` },
              { label: 'Positive Sentiment', value: `${dashboard.positive_sentiment_pct.toFixed(0)}%` },
              { label: 'Active Issues', value: dashboard.active_issues.toString() },
              { label: 'Critical Issues', value: dashboard.critical_issues.toString() },
              { label: 'Avg Return Rate', value: `${dashboard.avg_return_rate_pct.toFixed(1)}%` },
              { label: 'Review Velocity', value: `${dashboard.review_velocity_per_day.toFixed(1)}/day` },
            ].map((kpi) => (
              <div key={kpi.label} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <p className="text-xs font-medium uppercase text-slate-500">{kpi.label}</p>
                <p className="mt-1 text-2xl font-semibold text-slate-900">{kpi.value}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === 'reviews' && (
        <div className="space-y-4">
          {reviews.map((r) => (
            <div key={r.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex justify-between items-start gap-4">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-amber-500 text-sm">{stars(r.rating)}</span>
                    <span className={`rounded px-2 py-0.5 text-xs capitalize ${sentimentClass(r.sentiment)}`}>{r.sentiment}</span>
                    {r.verified && <span className="text-xs text-slate-400">Verified</span>}
                  </div>
                  <h3 className="font-medium text-slate-900 mt-1">{r.title}</h3>
                  <p className="text-sm text-slate-600 mt-1">{r.body}</p>
                  <p className="text-xs text-slate-400 mt-2">{r.sku} · {r.product_name}</p>
                </div>
              </div>
              {r.topics && r.topics.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {r.topics.map((t) => (
                    <span key={t} className="rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-600 capitalize">{t.replace(/_/g, ' ')}</span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === 'sentiment' && (
        <div className="grid gap-4 sm:grid-cols-2">
          {sentiment.map((s) => (
            <div key={s.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="font-medium text-slate-900">{s.sku}</h3>
                  <p className="text-sm text-slate-500">{s.category}</p>
                </div>
                <span className={`rounded px-2 py-0.5 text-xs capitalize ${sentimentClass(s.overall_sentiment)}`}>{s.overall_sentiment}</span>
              </div>
              <div className="mt-3 grid grid-cols-3 gap-2 text-sm">
                <div><p className="text-emerald-600 font-medium">{s.positive_pct.toFixed(0)}%</p><p className="text-xs text-slate-400">Positive</p></div>
                <div><p className="text-slate-600 font-medium">{s.neutral_pct.toFixed(0)}%</p><p className="text-xs text-slate-400">Neutral</p></div>
                <div><p className="text-red-600 font-medium">{s.negative_pct.toFixed(0)}%</p><p className="text-xs text-slate-400">Negative</p></div>
              </div>
              <p className="mt-2 text-xs text-slate-500">{s.review_count.toLocaleString()} reviews · {s.avg_rating.toFixed(1)}★ · Trend: {s.trend_direction}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'topics' && (
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-2">Topic</th>
                <th className="px-4 py-2">Mentions</th>
                <th className="px-4 py-2">Sentiment</th>
                <th className="px-4 py-2">Trend</th>
                <th className="px-4 py-2">Sample Phrases</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {topics.map((t) => (
                <tr key={t.id}>
                  <td className="px-4 py-2 font-medium">{t.name}</td>
                  <td className="px-4 py-2">{t.mention_count.toLocaleString()}</td>
                  <td className="px-4 py-2"><span className={`rounded px-2 py-0.5 text-xs capitalize ${sentimentClass(t.sentiment)}`}>{t.sentiment}</span></td>
                  <td className="px-4 py-2">{t.trend_pct != null && t.trend_pct > 0 ? '+' : ''}{t.trend_pct?.toFixed(0)}%</td>
                  <td className="px-4 py-2 text-slate-500 text-xs">{t.sample_phrases?.slice(0, 2).join(', ')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'issues' && (
        <div className="space-y-3">
          {issues.map((i) => (
            <div key={i.id} className={`rounded-xl border p-4 ${severityClass(i.severity)}`}>
              <div className="flex justify-between items-start gap-4">
                <div>
                  <span className="text-xs font-medium uppercase">{i.severity} · {i.type.replace(/_/g, ' ')}</span>
                  <h3 className="font-medium mt-1">{i.title}</h3>
                  {i.description && <p className="text-sm mt-1">{i.description}</p>}
                  {i.evidence && <p className="text-xs mt-1 opacity-75">{i.evidence}</p>}
                </div>
                <div className="text-right shrink-0 text-sm">
                  <p className="font-semibold">{i.occurrences} occurrences</p>
                  <p className="capitalize text-xs">{i.status}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'returns' && (
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-2">SKU</th>
                <th className="px-4 py-2">Return Rate</th>
                <th className="px-4 py-2">Count</th>
                <th className="px-4 py-2">Top Reason</th>
                <th className="px-4 py-2">Root Cause</th>
                <th className="px-4 py-2">Trend</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {returns.map((r) => (
                <tr key={r.id}>
                  <td className="px-4 py-2 font-medium">{r.sku}</td>
                  <td className="px-4 py-2">{r.return_rate_pct.toFixed(1)}%</td>
                  <td className="px-4 py-2">{r.return_count}</td>
                  <td className="px-4 py-2">{r.top_reason ?? '—'}</td>
                  <td className="px-4 py-2 text-slate-600">{r.root_cause ?? '—'}</td>
                  <td className="px-4 py-2 capitalize">{r.trend_direction ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'vocabulary' && (
        <div className="grid gap-4 sm:grid-cols-2">
          {vocabulary.map((v) => (
            <div key={v.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <h3 className="font-medium text-slate-900">{v.sku ?? v.category}</h3>
              {v.top_words && (
                <div className="mt-3">
                  <p className="text-xs font-medium uppercase text-slate-500">Top Words</p>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {v.top_words.map((w) => <span key={w} className="rounded bg-sky-50 px-2 py-0.5 text-xs text-sky-700">{w}</span>)}
                  </div>
                </div>
              )}
              {v.pain_points && v.pain_points.length > 0 && (
                <div className="mt-3">
                  <p className="text-xs font-medium uppercase text-slate-500">Pain Points</p>
                  <ul className="mt-1 text-sm text-red-600 list-disc list-inside">{v.pain_points.map((p) => <li key={p}>{p}</li>)}</ul>
                </div>
              )}
              {v.positive_expressions && v.positive_expressions.length > 0 && (
                <div className="mt-3">
                  <p className="text-xs font-medium uppercase text-slate-500">Positive Expressions</p>
                  <ul className="mt-1 text-sm text-emerald-600 list-disc list-inside">{v.positive_expressions.slice(0, 3).map((p) => <li key={p}>{p}</li>)}</ul>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === 'recommendations' && (
        <div className="space-y-4">
          <button type="button" onClick={generateRecs} disabled={busy}
            className="rounded-lg bg-sky-600 px-4 py-2 text-sm font-medium text-white hover:bg-sky-700 disabled:opacity-50">
            Generate AI Recommendations
          </button>
          {recommendations.map((rec) => (
            <div key={rec.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <span className="text-xs font-medium text-sky-600">#{rec.priority_rank} · {rec.type.replace(/_/g, ' ')}</span>
                  <h3 className="font-medium text-slate-900">{rec.title}</h3>
                  {rec.description && <p className="mt-1 text-sm text-slate-600">{rec.description}</p>}
                  {rec.evidence && <p className="mt-1 text-xs text-slate-500">{rec.evidence}</p>}
                </div>
                <div className="text-right text-sm">
                  <p className="font-medium">{(rec.confidence_score * 100).toFixed(0)}% confidence</p>
                  {rec.expected_impact && <p className="text-slate-500">{rec.expected_impact}</p>}
                  {rec.requires_approval && <p className="text-amber-600 text-xs">Requires approval</p>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'timeline' && (
        <div className="space-y-3">
          {timeline.map((t) => (
            <div key={t.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm flex gap-4">
              <div className="w-2 rounded-full bg-sky-400 shrink-0" />
              <div>
                <span className="text-xs text-sky-600 capitalize">{t.event_type.replace(/_/g, ' ')}</span>
                <h3 className="font-medium text-slate-900">{t.title}</h3>
                {t.summary && <p className="text-sm text-slate-600">{t.summary}</p>}
                {t.sku && <p className="text-xs text-slate-400 mt-1">{t.sku}</p>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
